/**
 * L2: wallet switcher (01-references §6 S4). It copies the 1Password account/collection switcher
 * at the top left and carries two Quick Search behaviours: time left unlocked is visible (rule
 * 10), and a failed wallet reads as zero instead of hiding the others (rule 11).
 */
import type { VaultSnapshot, WalletId } from "../contract.js";
import { walletCounts } from "../search/rank.js";
import { replicaAge, type ReplicaAge } from "./staleness.js";

export interface SwitcherRow {
  id: WalletId | null;
  name: string;
  count: number;
  selected: boolean;
  failed: string | null;
}

export interface SwitcherModel {
  rows: SwitcherRow[];
  unlockedFor: string | null;
  replica: ReplicaAge;
  fixture: boolean;
}

export function switcherModel(snapshot: VaultSnapshot, scope: WalletId | null, unlockedAt: number | null, now: number): SwitcherModel {
  const counts = walletCounts(snapshot);
  const failures = new Map(snapshot.failedWallets.map((f) => [f.wallet, f.reason]));
  const walletRows: SwitcherRow[] = snapshot.wallets.map((w) => ({
    id: w.id,
    name: w.name,
    count: counts.get(w.id) ?? 0,
    selected: scope === w.id,
    failed: failures.get(w.id) ?? null,
  }));
  return {
    rows: [
      { id: null, name: "All Wallets", count: walletRows.reduce((a, r) => a + r.count, 0), selected: scope === null, failed: null },
      ...walletRows,
    ],
    unlockedFor: unlockedAt === null ? null : formatDuration(now - unlockedAt),
    replica: replicaAge(snapshot.asOf, now),
    fixture: snapshot.fixture,
  };
}

export function formatDuration(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "less than a minute";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}
